import type { Property } from "@repo/database/types";
import React from "react";

/**
 * Has the active property been decided yet?
 *
 * `loaded` is what consumers gate subject-aware rendering on, so it has to
 * mean "the answer is in", never "nothing came back". Three inputs:
 *
 *   - no organization id yet → the property list has not even been asked for.
 *     That is loading, unless the active organization has itself resolved to
 *     nothing (a user with no organization), in which case there is nothing
 *     left to wait for.
 *   - organization id known → the list query's own pending state decides, and
 *     so does the lookup for a property the list leaves out (see
 *     `resolveUnlistedProperty`).
 *
 * The middle case is the one that regressed: the list query used to fire with
 * an empty organization id on the first render of a cold-loaded deep link, get
 * a 403, and settle — leaving `loaded` true with no property, which consumers
 * read as "no properties" and, since the subject type comes from the property,
 * as BRAND.
 */
export function isActivePropertyLoaded(input: {
	organizationId: string | undefined;
	organizationLoaded: boolean;
	propertiesPending: boolean;
	unlistedPropertyPending?: boolean;
}): boolean {
	if (!input.organizationId) {
		return input.organizationLoaded;
	}
	return !input.propertiesPending && !input.unlistedPropertyPending;
}

/**
 * The active property when it isn't in the organization's property list.
 *
 * Prospects stay out of that list so they don't reach the switcher, the Clients
 * grid or the workflow pickers, but someone can still open one. Only a prospect
 * of this organization resolves: a hidden scoring-only brand has no dashboard
 * to show, and a property of another organization doesn't belong under this
 * organization's URL.
 */
export function resolveUnlistedProperty<
	T extends { isProspect: boolean; organizationId: string },
>(
	property: T | null | undefined,
	organizationId: string | undefined,
): T | null {
	if (!property?.isProspect || property.organizationId !== organizationId) {
		return null;
	}
	return property;
}

export const ActivePropertyContext = React.createContext<
	| {
			activeProperty: Property | null;
			loaded: boolean;
			setActiveProperty: (
				propertyId: string | null,
				/** `navigate: false` records the choice without routing to the property home. */
				options?: { navigate?: boolean },
			) => Promise<void>;
			refetchActiveProperty: () => Promise<void>;
	  }
	| undefined
>(undefined);
